import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import { Loading } from './components/UI';

// Pages
import { Login, Register } from './pages/Auth';
import Dashboard from './pages/Dashboard';
import {
  Clients as ClientsPage,
  Assets as AssetsPage,
  Drivers as DriversPage,
  Vehicles as VehiclesPage,
  Tenders as TendersPage,
  Contracts as ContractsPage,
  Logistics as LogisticsPage,
  Maintenance as MaintenancePage,
  Financial as FinancialPage,
  Invoices as InvoicesPage,
  Documents as DocumentsPage,
} from './pages/Modules';
import UsersPage from './pages/Users';
import SettingsPage from './pages/Settings';
import SuperAdminPortal from './pages/SuperAdmin';
import DriverApp from './pages/DriverApp';
import CommunicationsPage from './pages/Communications';
import { HeatmapPage, AuditPage } from './pages/Analytics';

function PrivateRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !['admin','ADMIN','superadmin','SUPERADMIN'].includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function Layout({ children }) {
  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        {children}
      </main>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return <Loading />;

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/register" element={user ? <Navigate to="/" replace /> : <Register />} />

      {/* Protected */}
      <Route path="/" element={
        <PrivateRoute>
          <Layout><Dashboard /></Layout>
        </PrivateRoute>
      } />

      <Route path="/clients" element={
        <PrivateRoute>
          <Layout><ClientsPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/assets" element={
        <PrivateRoute>
          <Layout><AssetsPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/drivers" element={
        <PrivateRoute>
          <Layout><DriversPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/vehicles" element={
        <PrivateRoute>
          <Layout><VehiclesPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/tenders" element={
        <PrivateRoute>
          <Layout><TendersPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/contracts" element={
        <PrivateRoute>
          <Layout><ContractsPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/logistics" element={
        <PrivateRoute>
          <Layout><LogisticsPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/maintenance" element={
        <PrivateRoute>
          <Layout><MaintenancePage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/financial" element={
        <PrivateRoute>
          <Layout><FinancialPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/invoices" element={
        <PrivateRoute>
          <Layout><InvoicesPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/documents" element={
        <PrivateRoute>
          <Layout><DocumentsPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/users" element={
        <PrivateRoute adminOnly>
          <Layout><UsersPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/settings" element={
        <PrivateRoute adminOnly>
          <Layout><SettingsPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/heatmap" element={
        <PrivateRoute>
          <Layout><HeatmapPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/audit" element={
        <PrivateRoute>
          <Layout><AuditPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/communications" element={
        <PrivateRoute>
          <Layout><CommunicationsPage /></Layout>
        </PrivateRoute>
      } />

      <Route path="/motorista" element={
        <PrivateRoute>
          <DriverApp />
        </PrivateRoute>
      } />

      <Route path="/superadmin" element={
        <PrivateRoute>
          <Layout><SuperAdminPortal /></Layout>
        </PrivateRoute>
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: {
              borderRadius: '8px',
              fontSize: '14px',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
